import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import { isEmpty, isNil } from "lodash";
import type { Job } from "../../types/queue-types";

type JobDetailModalProps = {
  readonly job: Job | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onRetry: (jobId: string) => void;
  readonly onRemove: (jobId: string) => void;
  readonly isFailed: boolean;
};

const formatTimestamp = (timestamp: number | undefined): string => {
  if (isNil(timestamp)) {
    return "-";
  }
  return new Date(timestamp).toLocaleString();
};

const JobDetailModal = ({
  job,
  open,
  onClose,
  onRetry,
  onRemove,
  isFailed,
}: JobDetailModalProps) => {
  if (isNil(job)) {
    return null;
  }

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>
        Job Details: {job.name} (ID: {job.id})
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Box>
            <Typography color="text.secondary" variant="subtitle2">
              Progress
            </Typography>
            <Typography>{job.progress}%</Typography>
          </Box>

          <Box>
            <Typography color="text.secondary" variant="subtitle2">
              Attempts Made
            </Typography>
            <Typography>{job.attemptsMade}</Typography>
          </Box>

          <Divider />

          <Typography fontWeight="bold" variant="subtitle1">
            Timestamps
          </Typography>

          <Box sx={{ display: "flex", gap: 4 }}>
            <Box>
              <Typography color="text.secondary" variant="subtitle2">
                Created
              </Typography>
              <Typography>{formatTimestamp(job.timestamp)}</Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="subtitle2">
                Processed
              </Typography>
              <Typography>{formatTimestamp(job.processedOn)}</Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="subtitle2">
                Finished
              </Typography>
              <Typography>{formatTimestamp(job.finishedOn)}</Typography>
            </Box>
          </Box>

          <Divider />

          <Typography fontWeight="bold" variant="subtitle1">
            Job Data
          </Typography>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#f5f5f5",
              overflow: "auto",
              maxHeight: 300,
            }}
          >
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
              {JSON.stringify(job.data, null, 2)}
            </pre>
          </Paper>

          {isFailed &&
            !isNil(job.failedReason) &&
            job.failedReason.length > 0 && (
              <>
                <Divider />
                <Typography color="error" fontWeight="bold" variant="subtitle1">
                  Error
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: "#ffebee",
                    overflow: "auto",
                  }}
                >
                  <Typography color="error">{job.failedReason}</Typography>
                </Paper>
              </>
            )}

          {isFailed && !isNil(job.stacktrace) && !isEmpty(job.stacktrace) && (
            <>
              <Typography color="error" fontWeight="bold" variant="subtitle1">
                Stack Trace
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: "#ffebee",
                  overflow: "auto",
                  maxHeight: 200,
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    fontSize: "0.8rem",
                    color: "#c62828",
                  }}
                >
                  {job.stacktrace.join("\n")}
                </pre>
              </Paper>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {isFailed && (
          <Button
            color="primary"
            startIcon={<ReplayIcon />}
            onClick={() => {
              onRetry(job.id);
            }}
          >
            Retry
          </Button>
        )}
        <Button
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => {
            onRemove(job.id);
          }}
        >
          Remove
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobDetailModal;
